/* eslint-disable @typescript-eslint/no-explicit-any */
import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  InfiniteFindByQueryOptions,
  OrderByInput,
  SortDirectionEnum,
  useGrippContext,
  useInfiniteFindByQuery,
} from '@gripp/shared-logic';
import {
  ColumnDef,
  Row,
  RowSelectionState,
  TableState,
  Updater,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useFocusEffect } from 'expo-router';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  CursorValue,
  DimensionValue,
  Keyboard,
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  RefreshControl,
  ScrollView,
  StyleProp,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { ActivityIndicator, DataTable, Text } from 'react-native-paper';
import { LoadingIndicator } from '../components/loadingIndicator';
import { useScrollXPosition } from '../hooks';
import { Colors } from '../themes';
import { DataGridExportButton } from './dataGridExport';

export type InfiniteDataGridProps = InfiniteFindByQueryOptions & {
  columns: ColumnDef<any, any>[];
  /**
   * Optional list of columns to use for data export.
   * If not provided, the visible `columns` list will be used.
   * This allows consumers to add export-only (hidden) columns without
   * affecting the rendered grid.
   */
  exportColumns?: ColumnDef<any, any>[];
  onRowClick?: (row: any) => void;
  listMaxHeight?: number;
  paddingBottom?: number;
  hideHeader?: boolean;
  showScrollIndicator?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  rowStyle?: (row: any) => StyleProp<ViewStyle>;
  textEmpty?: string | ReactNode;
  onItemsCountChange?: (count: number) => void;
  dataPrefix?: string;
  onOrderByChange?: (order: OrderByInput) => void;
  updateRowsSelected?: (rows: Array<any>) => void;
  isRowSelected?: (row: any) => boolean;
  enableScrollRefresh?: boolean;
  refetchOnFocus?: boolean;
  rightButtonSelectedId?: (id: string | undefined) => void;
  rightButtonHoverId?: (id: string | undefined) => void;
  preserveScrollKey?: string;
  initialState?: Partial<TableState>;
  headerContent?: ReactNode;
  exportFileName?: string;
  mobileTableHeader?: ReactNode;
  hideResultsLabel?: boolean;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  onDataLoaded?: (data: any) => void;
  showEmptyTable?: boolean;
  /**
   * Disable horizontal scrolling of the grid.
   * Useful when using swipeable items inside the grid to prevent gesture conflicts.
   */
  disableHorizontalScroll?: boolean;
  highlightSelectedRow?: boolean;
  selectedRowId?: string;
  disableRipple?: boolean;
};

interface CustomColumnMeta {
  style?: ViewStyle;
  headerStyle?: ViewStyle;
  customRender?: boolean;
  sortable?: boolean;
}

type CustomColumnDef = ColumnDef<any, any> & {
  accessorKey?: string;
  meta?: CustomColumnMeta;
};

interface ScrollPreservationData {
  itemId: string;
  scrollPosition: number;
}

type QueryData = {
  data: {
    pages: {
      count: number;
      limit: number;
      offset: number;
      items: never[];
    }[];
    pageParams: {
      offset: number;
    }[];
  };
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: any;
  refetch: any;
};
export type SelectionStatus = 'idle' | 'reset' | 'update-pending';

export const InfiniteDataGridOld = ({
  showEmptyTable = false,
  ...props
}: InfiniteDataGridProps) => {
  const [dataRows, setDataRows] = useState([]);
  const [currentPage, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState<OrderByInput>(props.orderBy);
  const { t } = useTranslation();
  const [isHoverStyle, setIsHoverStyle] = useState(false);
  const [rowHoverId, setRowHoverId] = useState<number | null>(null);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [refreshing, setRefreshing] = useState(false);
  const [rowSelectedId, setRowSelectedId] = useState<string | undefined>(
    undefined
  );
  const [scrollPosition, setScrollPosition] = useState(0);
  const { storageHandler } = useGrippContext();

  const VScrollRef = useRef<ScrollView | null>(null);
  const HScrollRef = useRef<ScrollView | null>(null);

  const {
    data,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteFindByQuery({
    ...props,
    pageSize: props.pageSize || 50,
    orderBy,
  }) as QueryData;
  const { onUpdatePositionX, onUpdateHasUpdatedData } = useScrollXPosition(
    isLoading,
    dataRows,
    HScrollRef
  );

  if (Platform.OS !== 'web') {
    useFocusEffect(
      useCallback(() => {
        if (props.refetchOnFocus && typeof refetch === 'function') {
          refetch();
        }
      }, [props.refetchOnFocus, refetch])
    );
  }

  useEffect(() => {
    if (
      props.orderBy?.field !== orderBy?.field ||
      props.orderBy?.order !== orderBy?.order
    ) {
      setOrderBy(props.orderBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderBy]);

  useEffect(() => {
    if (data) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy]);

  useEffect(() => {
    if (data?.pages) {
      props.onItemsCountChange && props.onItemsCountChange(data.pages[0].count);
      props.onDataLoaded && props.onDataLoaded(data.pages[0].items);
    }
  }, [data, props]);

  // restore scroll position, if requested
  useEffect(() => {
    const restoreScrollPosition = async () => {
      if (!props.preserveScrollKey) return;

      const scrollDataString = await storageHandler.getItem(
        props.preserveScrollKey
      );

      let yOffset = 0;
      if (scrollDataString) {
        const scrollData = JSON.parse(
          scrollDataString
        ) as ScrollPreservationData;
        yOffset = scrollData.scrollPosition;
      }

      setTimeout(() => {
        VScrollRef.current?.scrollTo({ y: yOffset, animated: false });
      }, 300);
    };
    restoreScrollPosition();
  }, [props.preserveScrollKey, storageHandler]);

  useEffect(() => {
    if (props?.selectionStatus === 'reset') {
      setRowSelection({});
      props.onSelectionStatusChange?.('idle');
    }
  }, [props?.selectionStatus, props?.onSelectionStatusChange]);

  const handleRowSelected = (updater: Updater<RowSelectionState>) => {
    const newSelection =
      typeof updater === 'function' ? updater(rowSelection) : updater;
    setRowSelection(newSelection);

    if (props.updateRowsSelected) {
      const selectedRowIndexs = Object.keys(newSelection).filter(
        (key: string) => newSelection[key]
      );
      const selectedRowData = dataRows.filter((row, index) =>
        selectedRowIndexs.includes(index.toString())
      );

      props.updateRowsSelected(selectedRowData);
    }
  };

  const table = useReactTable({
    data: dataRows,
    columns: props.columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: handleRowSelected,
    state: {
      rowSelection,
      ...props.initialState,
    },
  });

  const onScrollEnd = () => {
    if (hasNextPage && !isFetchingNextPage) {
      setPage(currentPage + 1);
      fetchNextPage();
    }
  };

  const isCloseToBottom = ({
    layoutMeasurement,
    contentOffset,
    contentSize,
  }: NativeScrollEvent) => {
    const paddingToBottom = 20;
    return (
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom
    );
  };

  const sortColumn = (column: CustomColumnDef) => {
    if (!column.meta?.sortable) {
      return;
    }

    const field = column.accessorKey || '';
    let order = SortDirectionEnum.Desc;

    if (orderBy?.field === field && orderBy.order === SortDirectionEnum.Desc) {
      order = SortDirectionEnum.Asc;
    }

    setOrderBy({ field, order });
    props.onOrderByChange && props.onOrderByChange({ field, order });
    onUpdateHasUpdatedData(true);
  };

  const renderSortArrow = (column: CustomColumnDef) => {
    if (orderBy?.field === column.accessorKey) {
      return orderBy.order === SortDirectionEnum.Asc ? (
        <MaterialCommunityIcons name="arrow-down" size={16} />
      ) : (
        <MaterialCommunityIcons name="arrow-up" size={16} />
      );
    }
    return null;
  };

  useMemo(() => {
    if (data) {
      const items = [].concat(...data.pages.map((page) => page.items));
      setDataRows(items);
    }

    // reset row selections on any change with table data (ex: sort and filter)
    if (data && data.pages.length < 2 && props.updateRowsSelected) {
      if (props.isRowSelected) {
        const items = [].concat(...data.pages.map((page) => page.items));

        const selected: any[] = [];
        const unselected: any[] = [];

        if (props.isRowSelected) {
          for (const item of items) {
            if (props.isRowSelected(item)) {
              selected.push(item);
            } else {
              unselected.push(item);
            }
          }
        } else {
          unselected.push(...items);
        }

        const sortedItems: any = [...selected, ...unselected];
        setDataRows(sortedItems);
        const newSelection: RowSelectionState = {};

        for (let i = 0; i < sortedItems.length; i++) {
          newSelection[i] = props.isRowSelected(sortedItems[i]);
        }
        setRowSelection(newSelection);
      } else {
        setRowSelection({});
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [containerWidth, setContainerWidth] = useState<DimensionValue>('100%');
  const [hScrollViewWidth, setHScrollViewWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined
  );

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    setHScrollViewWidth(width);
  };

  useEffect(() => {
    if (hScrollViewWidth && props.columns) {
      const totalSize = table.getTotalSize();
      if (totalSize > hScrollViewWidth) {
        setContainerWidth(totalSize);
      } else {
        setContainerWidth('100%');
      }
    }
  }, [hScrollViewWidth, props.columns, table]);

  const onRefresh = () => {
    setRefreshing(true);
    refetch().then(() => {
      setRefreshing(false);
    });
  };

  const onRowClick = async (row: Row<any>) => {
    if (props.preserveScrollKey) {
      const scrollData: ScrollPreservationData = {
        itemId: row.original.id,
        scrollPosition: scrollPosition,
      };

      await storageHandler.setItem(
        props.preserveScrollKey,
        JSON.stringify(scrollData)
      );
    }
    props.onRowClick && props.onRowClick(row.original);
  };

  const handleScroll = (nativeEvent: NativeScrollEvent, refName: string) => {
    if (refName === 'hScroll') {
      onUpdatePositionX(nativeEvent.contentOffset.x);
    }

    if (isCloseToBottom(nativeEvent)) {
      onScrollEnd();
    }
    if (props.preserveScrollKey) {
      setScrollPosition(nativeEvent.contentOffset.y);
    }
  };

  const TableHeader: FC<{ position: string }> = ({ position }) => {
    const headers = table.getFlatHeaders();

    return (
      <DataTable.Header
        style={[
          styles.header,
          {
            width: containerWidth,
          },
        ]}
      >
        {headers.map((header) => {
          return (
            <DataTable.Title
              onPress={() =>
                sortColumn(header.column.columnDef as CustomColumnDef)
              }
              key={header.id}
              style={[
                header.id === 'selected' && styles.checkboxHeaderContainer,
                (header.column.columnDef.meta as CustomColumnMeta)?.headerStyle,
                {
                  cursor: (header.column.columnDef.meta as CustomColumnMeta)
                    ?.sortable
                    ? 'pointer'
                    : ('default' as CursorValue),
                },
              ]}
            >
              <Text style={styles.titleText}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </Text>
              {renderSortArrow(header.column.columnDef as CustomColumnDef)}
            </DataTable.Title>
          );
        })}
      </DataTable.Header>
    );
  };

  const renderResult = (isShown = false) => {
    if (!data || !data.pages || !data.pages[0]) {
      return null;
    }

    const count = data.pages[0].count;

    return count !== undefined && count !== null && isShown ? (
      <Text style={styles.resultLabel}>
        {count} {t('activity.topBar.results')}
      </Text>
    ) : null;
  };

  const showHeader =
    (Platform.OS === 'web' || !props.hideHeader) && !props.hideResultsLabel;

  return (
    <View
      style={[
        styles.container,
        styles.containerFlex,
        props.containerStyle,
        {
          overflow: 'hidden',
          ...(props.listMaxHeight !== undefined
            ? { maxHeight: props.listMaxHeight }
            : {}),
        },
      ]}
      onLayout={(e) => {
        if (props.listMaxHeight === undefined) {
          setContainerHeight(e.nativeEvent.layout.height);
        }
      }}
    >
      {isLoading ? (
        <View style={styles.noResultsContainer}>
          <LoadingIndicator />
        </View>
      ) : !dataRows?.length && !showEmptyTable ? (
        <View style={styles.noResultsContainer}>
          <Text style={styles.emptyRow}>
            {props?.textEmpty ? props?.textEmpty : t('textEmptyList')}
          </Text>
        </View>
      ) : (
        <>
          {showHeader && (
            <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
              <View style={styles.tableHeaderContainer}>
                {renderResult(true)}
                {props.headerContent && props.headerContent}
                {props.exportFileName && Platform.OS === 'web' && (
                  <View style={{ marginLeft: 'auto' }}>
                    <DataGridExportButton
                      fileName={props.exportFileName}
                      query={props.query}
                      filter={props.filter}
                      orderBy={props.orderBy}
                      dataPrefix={props.dataPrefix!}
                      // Prefer dedicated export columns when provided so that
                      // hidden/export-only columns can be included in CSV.
                      columns={props.exportColumns ?? props.columns}
                    />
                  </View>
                )}
              </View>
            </TouchableWithoutFeedback>
          )}
          <DataTable>
            <View
              key={'tableView'}
              style={(() => {
                const effectiveHeight = props.listMaxHeight ?? containerHeight;
                if (effectiveHeight !== undefined) {
                  const headerHeight = styles.tableHeaderContainer.height;
                  return {
                    height: showHeader
                      ? Math.max(0, effectiveHeight - headerHeight)
                      : effectiveHeight,
                  };
                }
                return { flex: 1 };
              })()}
            >
              <ScrollView
                horizontal
                scrollEnabled={props.disableHorizontalScroll !== true}
                scrollEventThrottle={16}
                key={'HScrollView'}
                ref={HScrollRef}
                showsHorizontalScrollIndicator={
                  props.disableHorizontalScroll !== true
                }
                onLayout={handleLayout}
                onScroll={({ nativeEvent }) =>
                  handleScroll(nativeEvent, 'hScroll')
                }
                onTouchStart={(e) => e.stopPropagation()}
                contentContainerStyle={(() => {
                  const effectiveHeight =
                    props.listMaxHeight ?? containerHeight;
                  const headerHeight = styles.tableHeaderContainer.height;
                  const scrollHeight =
                    effectiveHeight !== undefined
                      ? showHeader
                        ? Math.max(0, effectiveHeight - headerHeight)
                        : effectiveHeight
                      : undefined;
                  return {
                    width: containerWidth,
                    flexDirection: 'column' as const,
                    ...(scrollHeight !== undefined
                      ? { height: scrollHeight }
                      : { flex: 1 }),
                  };
                })()}
              >
                {!props.hideHeader && <TableHeader position="center" />}
                {showEmptyTable && !dataRows?.length ? (
                  props.textEmpty || null
                ) : (
                  <ScrollView
                    ref={VScrollRef}
                    style={{ flex: 1 }}
                    contentContainerStyle={[
                      styles.scrollView,
                      {
                        paddingBottom: props.paddingBottom ?? 0,
                      },
                    ]}
                    showsVerticalScrollIndicator={props.showScrollIndicator}
                    scrollEventThrottle={10}
                    refreshControl={
                      props.enableScrollRefresh === true ? (
                        <RefreshControl
                          refreshing={refreshing}
                          onRefresh={onRefresh}
                        />
                      ) : undefined
                    }
                    onScroll={(
                      event: NativeSyntheticEvent<NativeScrollEvent>
                    ) => {
                      handleScroll(event.nativeEvent, 'vScroll');
                    }}
                  >
                    {Platform.OS !== 'web' && props.hideHeader && (
                      <View
                        style={[
                          styles.tableHeaderContainer,
                          props.mobileTableHeader === undefined && {
                            height: 0,
                            paddingVertical: 0,
                            borderTopWidth: 0,
                            borderBottomWidth: 0,
                          },
                        ]}
                      >
                        {props.mobileTableHeader !== undefined
                          ? props.mobileTableHeader
                          : renderResult(true)}
                      </View>
                    )}
                    {table.getRowModel().rows.map((row) => {
                      // Use selectedRowId prop if provided (from URL), otherwise use local state
                      const currentSelectedId =
                        props.selectedRowId || rowSelectedId;
                      const isRowHighlighted =
                        props.highlightSelectedRow &&
                        currentSelectedId === row.original.id;

                      return (
                        <DataTable.Row
                          {...(props.disableRipple
                            ? {
                                rippleColor: 'transparent',
                                underlayColor: 'transparent',
                              }
                            : {})}
                          style={[
                            (props.rowStyle && props.rowStyle(row)) ||
                              styles.row,
                            isRowHighlighted && styles.selectedRow,
                            isHoverStyle &&
                              rowHoverId === row.index &&
                              !isRowHighlighted &&
                              styles.hover,
                            props.rightButtonSelectedId &&
                              rowSelectedId === row.original.id &&
                              !isRowHighlighted &&
                              styles.selected,
                          ]}
                          key={row.id}
                          onPress={() => {
                            onRowClick(row);
                            props.rightButtonSelectedId &&
                              props.rightButtonSelectedId(row.original.id);
                            setRowSelectedId(row.original.id);
                          }}
                          onHoverIn={() => {
                            setIsHoverStyle(true);
                            setRowHoverId(Number(row.id));

                            if (Number(row.id) === row.index) {
                              props.rightButtonHoverId &&
                                props.rightButtonHoverId(row.original.id);
                            }
                          }}
                          onHoverOut={() => {
                            setIsHoverStyle(false);
                            setRowHoverId(null);
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <DataTable.Cell
                              key={cell.id}
                              style={[
                                (cell.column.columnDef.meta as CustomColumnMeta)
                                  ?.style,
                              ]}
                            >
                              {(cell.column.columnDef.meta as CustomColumnMeta)
                                ?.customRender ? (
                                flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )
                              ) : (
                                <>
                                  {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext()
                                  )}
                                </>
                              )}
                            </DataTable.Cell>
                          ))}
                        </DataTable.Row>
                      );
                    })}
                    {isFetchingNextPage && (
                      <View style={styles.activityIndicator}>
                        <ActivityIndicator animating={true} size="large" />
                      </View>
                    )}
                  </ScrollView>
                )}
              </ScrollView>
            </View>
          </DataTable>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    borderRadius: 0,
  },
  containerFlex: {
    flex: 1,
  },
  header: {
    backgroundColor: Colors.primaryGrayHeader,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    borderBottomWidth: 0,
    ...Platform.select({
      web: {
        paddingTop: 0,
        marginTop: 0,
        height: 'auto',
      },
      default: {
        paddingLeft: 20,
        height: 44,
      },
    }),
  },
  hover: {
    backgroundColor: Colors.secondaryGrayHeader,
  },
  selected: {
    backgroundColor: Colors.unreadBackground,
  },
  selectedRow: {
    backgroundColor: Colors.primaryGrayBackground,
  },
  checkboxHeaderContainer: {
    height: '100%',
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  titleText: {
    ...Platform.select({
      ios: {
        lineHeight: 20,
        fontWeight: '500',
      },
      android: {
        lineHeight: 20,
        fontWeight: '700',
      },
      web: {
        fontWeight: '500',
      },
    }),
    fontSize: 16,
    color: Colors.blackText,
    letterSpacing: -0.2,
  },
  row: {
    borderBottomWidth: 1,
    borderBottomColor: Colors.secondaryGrayHeader,
  },
  scrollView: {
    flexGrow: 1,
  },
  noResultsContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    backgroundColor: Colors.white,
  },
  emptyRow: {
    textAlign: 'center',
    paddingVertical: 8,
    fontSize: 17,
    fontWeight: '600',
    width: '100%',
  },
  tableHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: 50,
    paddingHorizontal: 16,
    ...Platform.select({
      web: {
        backgroundColor: Colors.secondaryGrayHeader,
      },
      default: {
        backgroundColor: Colors.white,
        borderBottomWidth: 1,
        borderTopWidth: 1,
        borderColor: Colors.secondaryGrayHeader,
      },
    }),
  },
  resultLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.grayText,
    letterSpacing: -0.25,
  },
  activityIndicator: {
    marginTop: 16,
  },
});
